/**
 * SheetChecklistCard
 *
 * Renders a manual review checklist for the currently-active sheet
 * — the line items an architect should verify by eye on permit
 * sheets Vera doesn't auto-check (site plan, roof plan, project
 * info). On sheets Vera DOES check (the floor plan today) the card
 * renders a tiny "Vera is auto-checking this sheet" note instead,
 * so the architect knows the violations panel is the source of
 * truth here.
 *
 * The checklist is local-only state: a checkbox per item that
 * survives clicks but not reloads. This is intentional — the
 * checklist is a real-time scope reminder for the pitch, not a
 * persistent review log. A production version would persist these
 * per ACC project + per user.
 */
import { useState } from "preact/hooks";
import type { ChecklistItem } from "../data/projects";
import { useCurrentProject } from "../stores/projectStore";
import { useViewerStore } from "../stores/viewerStore";
import { Card } from "./Card";

export function SheetChecklistCard() {
  const project = useCurrentProject();
  const currentSheetId = useViewerStore(s => s.currentSheetId);

  const sheet = project.sheets.find(s => s.id === currentSheetId);
  const items = project.manualChecklistBySheet?.[currentSheetId];

  if (!sheet) return null;

  const sheetName = sheet.name;
  const isVeraChecked = Object.values(
    project.defaultSheetByOntologyType
  ).includes(currentSheetId);

  // Vera-checked sheet → render a small confirmation card instead
  // of the checklist (so the row always exists, the architect always
  // knows where they stand).
  if (isVeraChecked && !items) {
    return (
      <Card title="Manual review" meta={sheetName} defaultOpen={false}>
        <div
          style={{
            fontSize: 12,
            color: "var(--vera-text-dim)",
            lineHeight: 1.5,
          }}
        >
          <span style={{ color: "#78c896", fontWeight: 600 }}>
            ✓ Vera is auto-checking this sheet.
          </span>{" "}
          The violations panel below is the audit trail. Click any row to zoom
          and inspect the cited element.
        </div>
      </Card>
    );
  }

  if (!items || items.length === 0) {
    // Non-Vera-checked sheet with no curated checklist (yet). Stay
    // honest: tell the architect we don't have items for this view
    // rather than rendering nothing.
    return (
      <Card title="Manual review" meta={sheetName} defaultOpen={false}>
        <div
          style={{
            fontSize: 12,
            color: "var(--vera-text-dim)",
            lineHeight: 1.5,
          }}
        >
          No manual checklist curated for this sheet yet. This sheet is part of
          the permit submission set but isn't auto-checked by Vera and isn't on
          the manual-review roadmap either.
        </div>
      </Card>
    );
  }

  return <ChecklistBody sheetName={sheetName} items={items} />;
}

function ChecklistBody({
  sheetName,
  items,
}: {
  sheetName: string;
  items: ChecklistItem[];
}) {
  // Track checked items by index (sheet doesn't change while this
  // body is mounted — the parent component re-mounts on sheet
  // change, resetting state, which is the desired behaviour).
  const [checked, setChecked] = useState<Set<number>>(() => new Set());
  const toggle = (idx: number) =>
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });

  const done = checked.size;
  const total = items.length;
  const summary = `${done}/${total} checked`;

  return (
    <Card
      title="Manual review checklist"
      meta={summary}
      defaultOpen
      accent="#e5b85f"
    >
      <div
        style={{
          fontSize: 11,
          color: "var(--vera-text-dim)",
          marginBottom: 8,
          lineHeight: 1.5,
        }}
      >
        Vera doesn't auto-check items on{" "}
        <strong style={{ color: "var(--vera-text)" }}>{sheetName}</strong>.
        Eyeball each item below before submitting; cites point at the most
        commonly-asked rule.
      </div>
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {items.map((item, idx) => {
          const isChecked = checked.has(idx);
          const inputId = `chk-${idx}`;
          return (
            <li key={item.label}>
              <label
                htmlFor={inputId}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  padding: "4px 6px",
                  borderRadius: 3,
                  cursor: "pointer",
                  background: isChecked
                    ? "rgba(120,200,150,0.08)"
                    : "transparent",
                  fontSize: 12,
                }}
              >
                <input
                  id={inputId}
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggle(idx)}
                  style={{ marginTop: 2, accentColor: "#78c896" }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      color: isChecked
                        ? "var(--vera-text-dim)"
                        : "var(--vera-text)",
                      textDecoration: isChecked ? "line-through" : "none",
                    }}
                  >
                    {item.label}
                  </span>
                  {item.cite && (
                    <span
                      style={{
                        marginLeft: 6,
                        fontSize: 10,
                        color: "var(--vera-text-dim)",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      · {item.cite}
                    </span>
                  )}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
